import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Store, ShoppingBag, Wrench, ShieldCheck, Settings as SettingsIcon, LogOut, BadgeCheck, Building2, ChevronRight, MessageSquare, HelpCircle, CreditCard, ClipboardList } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BusinessCard, ListingCard, ServiceCard } from "@/components/cards";
import { EmptyState, Spinner, PageLoading } from "@/components/common";
import { ManageListingCard } from "@/components/ManageListing";
import { PhoneVerifyDialog } from "@/components/dialogs";

const cnRow = (i) => "flex items-center gap-3 px-4 py-3.5 text-sm transition-colors hover:bg-secondary/50" + (i > 0 ? " border-t border-border" : "");

export default function Profile() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mine, setMine] = useState(null);
  const [savedCount, setSavedCount] = useState(null);
  const [reviewCount, setReviewCount] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const loadMine = () => Promise.all([
    api.get("/businesses", { params: { mine: true } }),
    api.get("/marketplace", { params: { mine: true } }),
    api.get("/services", { params: { mine: true } }),
  ]).then(([b, m, s]) => setMine({ businesses: b.data.items, marketplace: m.data.items, services: s.data.items }));
  useEffect(() => {
    loadMine();
    api.get("/favourites").then(({ data }) => setSavedCount((data.business?.length || 0) + (data.marketplace?.length || 0) + (data.service?.length || 0) + (data.request?.length || 0))).catch(() => setSavedCount(0));
    if (user?.id) api.get(`/auth/profile/${user.id}`).then(({ data }) => setReviewCount(data.reviews_written ?? 0)).catch(() => setReviewCount(0));
  }, [user?.id]);
  if (!user) return <PageLoading />;
  const memberSince = user.created_at ? new Date(user.created_at).toLocaleDateString("en-AU", { month: "short", year: "numeric" }) : "";
  const listingsCount = mine ? (mine.marketplace.length + mine.businesses.length + mine.services.length) : 0;
  const isStaff = ["owner", "admin", "moderator"].includes(user.role);

  const MENU = [
    { icon: ClipboardList, label: "My Listings", to: "#my-listings" },
    { icon: Heart, label: "Saved Items", to: "/saved", testid: "profile-saved" },
    { icon: MessageSquare, label: "Messages", to: "/messages" },
    { icon: BadgeCheck, label: "Verification", to: "/verification", testid: "profile-verify" },
    { icon: CreditCard, label: "Payments", to: "/settings" },
    { icon: SettingsIcon, label: "Settings", to: "/settings", testid: "profile-settings" },
    ...(isStaff ? [{ icon: Building2, label: "Admin dashboard", to: "/admin", testid: "profile-admin" }] : []),
    { icon: HelpCircle, label: "Help & Support", to: "/safety" },
  ];

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div className="rounded-3xl border border-border bg-card p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-primary/10 font-display text-2xl font-extrabold text-primary">
            {user.avatar_url ? <img src={user.avatar_url} alt={user.name} className="h-full w-full object-cover" /> : (user.name || "U")[0].toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="flex items-center gap-1.5 font-display text-xl font-bold">
              {user.name}
              {(user.identity_verified || user.phone_verified) && <BadgeCheck className="h-5 w-5 text-brand-green" />}
            </h1>
            <p className="text-sm text-muted-foreground">{user.suburb ? `${user.suburb}, ` : ""}{user.state ? `${user.state} · ` : ""}Member since {memberSince}</p>
            {!user.phone_verified && <button onClick={() => setPhoneOpen(true)} className="mt-1 inline-block rounded-full border border-dashed border-primary/40 px-2 py-0.5 text-[11px] font-medium text-primary" data-testid="verify-phone-cta">+ Verify phone</button>}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-3 divide-x divide-border border-y border-border py-4 text-center" data-testid="profile-stats">
          {[["Listings", listingsCount], ["Reviews", reviewCount ?? "—"], ["Saved", savedCount ?? "—"]].map(([label, val]) => (
            <div key={label}><p className="font-display text-2xl font-extrabold text-foreground">{val}</p><p className="text-xs text-muted-foreground">{label}</p></div>
          ))}
        </div>
        {user.bio && <p className="mt-3 text-sm text-foreground/80">{user.bio}</p>}
        <div className="mt-4 overflow-hidden rounded-2xl border border-border">
          {MENU.map((m, i) => { const Icon = m.icon; return (
            <Link key={m.label} to={m.to} data-testid={m.testid || `menu-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={cnRow(i)}>
              <Icon className="h-5 w-5 text-muted-foreground" />
              <span className="flex-1 font-medium text-foreground">{m.label}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Link>
          ); })}
          <button onClick={() => logout().then(() => nav("/"))} data-testid="profile-logout" className={cnRow(MENU.length) + " w-full text-left"}>
            <LogOut className="h-5 w-5 text-destructive" />
            <span className="flex-1 font-medium text-destructive">Sign out</span>
          </button>
        </div>
      </div>

      <h2 id="my-listings" className="font-display text-lg font-semibold">My listings</h2>
      {!mine ? <div className="flex justify-center py-10"><Spinner /></div> : (
        <Tabs defaultValue="marketplace">
          <TabsList><TabsTrigger value="marketplace" data-testid="my-tab-marketplace">Items</TabsTrigger><TabsTrigger value="businesses" data-testid="my-tab-businesses">Businesses</TabsTrigger><TabsTrigger value="services" data-testid="my-tab-services">Services</TabsTrigger></TabsList>
          <TabsContent value="marketplace">{mine.marketplace.length ? <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{mine.marketplace.map((l) => <ManageListingCard key={l.id} listing={l} onChanged={loadMine} onDeleted={loadMine} />)}</div> : <EmptyState icon={ShoppingBag} title="No items yet" action={<Link to="/post/sell"><Button>Sell something</Button></Link>} />}</TabsContent>
          <TabsContent value="businesses">{mine.businesses.length ? <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{mine.businesses.map((b) => <BusinessCard key={b.id} b={b} />)}</div> : <EmptyState icon={Store} title="No businesses yet" action={<Link to="/post/business"><Button>Add business</Button></Link>} />}</TabsContent>
          <TabsContent value="services">{mine.services.length ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{mine.services.map((s) => <ServiceCard key={s.id} s={s} />)}</div> : <EmptyState icon={Wrench} title="No services yet" action={<Link to="/post/service"><Button>Offer a service</Button></Link>} />}</TabsContent>
        </Tabs>
      )}
      <PhoneVerifyDialog open={phoneOpen} onOpenChange={setPhoneOpen} />
    </div>
  );
}
